// firestore
// low-level api to interface with the firestore db

// the only assumptions it makes about the data are that there is a user object with a `nodes` collection,
// and each node document has an id:string field.


import { User, UserId, INode, NodeId, NodeValue, FieldId, Pattern } from '../../types'
import { Datastore } from '../types'
import { Firebase, Firestore, Path, Collection, Document } from './types'


export class FirestoreDb implements Datastore {

  type = 'FirestoreDb'
  firebase: any
  firestore: Firestore
  // userId: UserId | undefined
  currentUserId: UserId | undefined

  constructor(firebase: Firebase) {
    this.firebase = firebase
    if (!this.firestore) {
      this.firestore = firebase.firestore()
    }
    // this.currentUserId = undefined
  }


  // -----------------------------------------------------
  // users
  // -----------------------------------------------------

  // can be undefined
  getCurrentUserId() {
    return this.currentUserId
  }

  // pass nothing for signout
  setCurrentUserId(userId?: UserId) {
    this.currentUserId = userId
  }

  // get user object from db, eg {id:'auzh432k'}, or undefined if not there
  async getUser(userId: UserId): Promise<User|undefined> {
    const path = `users/${userId}`
    console.log('firestore:getuser', path)
    const snapshot = await this.firestore.doc(path).get()
    const user = snapshot.data() as User|undefined // will be undefined if doc not there OR not allowed
    console.log(user)
    return user
  }

  // save user object to db
  // note: don't really need to save id to db, but can't save an empty object
  async createUser(user: User): Promise<User> {
    const path = `users/${user.id}`
    await this.firestore.doc(path).set(user) // note: promise resolves to void
    return user
  }

  // note: this doesn't delete the associated collections - must do manually.
  // see https://firebase.google.com/docs/firestore/manage-data/delete-data
  // could implement that here, but for the moment simpler to do from up above - 
  // see neomem:deleteUser.
  async deleteUser(userId: UserId) {
    const path = `users/${userId}`
    await this.firestore.doc(path).delete()
  }


  // -----------------------------------------------------
  // get nodes
  // -----------------------------------------------------

  // get list of nodes in db format
  // pattern can be eg {typeId:'place'}, {parentId:'welcome'}, combinations, etc
  // async getNodes(pattern: Pattern = {}): Promise<INode[]> {
  async getNodes(pattern: Pattern = {}): Promise<INode[]> {
    const path: Path = `users/${this.currentUserId}/nodes/`
    const collection: Collection = this.firestore.collection(path)
    // eg const query = collection.where('typeId', '==', 'action').where(...)
    let query = collection
    for (let key of Object.keys(pattern)) {
      const value = pattern[key]
      query = query.where(key, '==', value)
    }
    const snapshot: Document[] = await query.get()
    const nodes: INode[] = []
    snapshot.forEach(doc => { // note: no .map available
      const node = doc.data()
      node.id = doc.id // store the id in the object also - convenient for the ui
      nodes.push(node)
    })
    return nodes
  }

  async saveNodes(nodes: INode[]): Promise<void> {
    if (this.currentUserId) {
      for (let node of nodes) {
        this.createNode(node)
      }
    }
  }

  //. rather not implement this - just needed for localstorage - keep out of db interface?
  async clearNodes(): Promise<void> {
  }


  //----------------------------------------------------------------------
  // update db
  //----------------------------------------------------------------------

  async createNode(node: INode): Promise<INode> {
    if (this.currentUserId) {
      console.log('db create node', node)
      const path = `users/${this.currentUserId}/nodes/${node.id}`
      const obj = { ...node }
      // filter out any undefined props (else firestore gives error)
      for (let key of Object.keys(obj)) {
        if (obj[key] === undefined) {
          delete obj[key]
        }
      }
      await this.firestore.doc(path).set(obj)
    }
    return node
  }

  async deleteNode(nodeId: NodeId): Promise<void> {
    if (this.currentUserId) {
      const path = `users/${this.currentUserId}/nodes/${nodeId}`
      await this.firestore.doc(path).delete()
    }
  }

  // async getNode(nodeId: NodeId): Promise<INode> {
  //   const path = `users/${this.currentUserId}/nodes/${nodeId}`
  //   const snapshot = await this.firestore.doc(path).get()
  //   const node = snapshot.data()
  //   return node
  // }

  //  async getNodeValue(nodeId: NodeId, fieldId: FieldId): Promise<NodeValue> {
  //   const path = `users/${this.currentUserId}/nodes/${nodeId}`
  //   //.. do restricted query to just get the field needed - save bandwidth, eg if had a huge description field
  //   const snapshot = await this.firestore.doc(path).get()
  //   const node = snapshot.data()
  //   return node[fieldId]
  //   // const node = this.getNode(nodeId)
  //   // return node[fieldId]
  // }


  async setNodeValue(nodeId: NodeId, fieldId: FieldId, value: NodeValue): Promise<void> {
    if (this.currentUserId) {
      // const values = [{ [fieldId]: value }]
      // this.setNodeValues(nodeId, values)
      const path = `users/${this.currentUserId}/nodes/${nodeId}`
      if (value !== undefined) {
        const fieldValue = { [fieldId]: value }
        await this.firestore.doc(path).set(fieldValue, { merge: true })
      } else {
        await this.deleteNodeValue(nodeId, fieldId)
      }
    }
  }

  // this is more efficient than indiv `set` calls, unless firestore is able to bundle writes?
  // async setNodeValues(nodeId: NodeId, values: Values): Promise<void> {
  //   // if (this.currentUserId) {
  //   //   const path = `users/${this.currentUserId}/nodes/${nodeId}`
  //   //   for (let value of values) {
  //   //     if (value === undefined) {
  //   //       await this.deleteNodeValue(nodeId, values.id)
  //   //     }
  //   //   }
  //   //   await this.firestore.doc(path).set(values, { merge: true })
  //   // }
  // }

  async deleteNodeValue(nodeId: NodeId, fieldId: FieldId) {
    if (this.currentUserId) {
      const path = `users/${this.currentUserId}/nodes/${nodeId}`
      await this.firestore.doc(path).update({
        [fieldId]: this.firebase.firestore.FieldValue.delete(),
      })
    }
  }

}
